var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { TelemetryEvents } from '@employee-experience/common/lib/TelemetryEvents';
import { guid } from '../Shared/guid';
import axios from 'axios';
import { EventType } from '@employee-experience/common/lib/Models/UsageTelemetry';
export class HttpClient {
    constructor(telemetryClient, authClient = null, options = null) {
        this.options = {
            logPayload: false,
            correlationIdHeaderName: 'x-correlation-id',
            disableCorrelationId: false,
        };
        this.getChildInstance = (telemetryClient, authClient) => {
            return new HttpClient(telemetryClient, authClient, this.options);
        };
        this.telemetryClient = telemetryClient;
        this.authClient = authClient;
        this.options = Object.assign({}, this.options, options);
    }
    request(request, options, event) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const correlationId = this.telemetryClient.getCorrelationId();
                const correlationHeader = {};
                if (!this.options.disableCorrelationId && !(options === null || options === void 0 ? void 0 : options.disableCorrelationId)) {
                    correlationHeader[request.correlationIdHeaderName || this.options.correlationIdHeaderName || 'CorrelationId'] = correlationId;
                }
                const customTelemetryProperty = (_a = options === null || options === void 0 ? void 0 : options.customTelemetryProperties) !== null && _a !== void 0 ? _a : {};
                const requestTelemetryProperties = Object.assign({ method: request.method || 'get', url: request.url, requestId: guid(), resource: request.resource }, customTelemetryProperty);
                const requestOptions = Object.assign(Object.assign({}, request), { method: request.method || 'get', headers: Object.assign({
                        'Content-Type': 'application/json',
                    }, correlationHeader, request.headers, request.header) });
                try {
                    let token = request.accessToken;
                    if (!request.accessToken && request.resource && this.authClient) {
                        token = yield this.authClient.acquireToken(request.resource);
                    }
                    if (token) {
                        requestOptions.headers = Object.assign({}, requestOptions.headers, {
                            Authorization: `Bearer ${token}`,
                        });
                    }
                }
                catch (ex) {
                    reject({
                        status: -1,
                        statusText: TelemetryEvents.AcquireTokenFailed,
                        data: ex,
                    });
                }
                const startTime = new Date();
                try {
                    this.telemetryClient.trackTrace({
                        message: TelemetryEvents.APIRequestStarted,
                        properties: Object.assign(Object.assign({}, requestTelemetryProperties), this.getPayloadTelemetryProperty(this.options.logPayload, request.data)),
                    });
                    const response = yield axios.request(requestOptions);
                    const endTime = new Date();
                    const elapsed = endTime.getTime() - startTime.getTime();
                    const responseTelemetryProperties = Object.assign(Object.assign({}, requestTelemetryProperties), { status: response.status, statusText: response.statusText, elapsed });
                    this.telemetryClient.trackTrace({
                        message: TelemetryEvents.APIResponseReceived,
                        properties: Object.assign(Object.assign({}, responseTelemetryProperties), this.getPayloadTelemetryProperty(this.options.logPayload, response.data)),
                    });
                    const usageEvent = Object.assign(Object.assign({}, event), { type: EventType.System, timeTaken: elapsed, experienceResult: true, 
                        // Do not log the response data.
                        businessTransactionId: JSON.stringify(Object.assign({ status: response.status, correlationId }, requestTelemetryProperties)) });
                    this.telemetryClient.trackEvent(usageEvent);
                    resolve({
                        status: response.status,
                        statusText: response.statusText,
                        data: response.data,
                        headers: response.headers,
                    });
                }
                catch (e) {
                    const ex = e;
                    if (ex.response) {
                        const errorTelemetryProperty = Object.assign(Object.assign({}, requestTelemetryProperties), { status: ex.response.status, statusText: ex.response.statusText, error: ex.response.data });
                        this.telemetryClient.trackTrace({
                            message: TelemetryEvents.APIFailedResponseReceived,
                            properties: errorTelemetryProperty,
                        });
                        const usageEvent = Object.assign(Object.assign({}, event), { type: EventType.System, timeTaken: +new Date() - +startTime, experienceResult: false, businessTransactionId: JSON.stringify(Object.assign({ status: ex.response.status, correlationId }, requestTelemetryProperties)) });
                        this.telemetryClient.trackEvent(usageEvent);
                        if (!options || !options.silentError) {
                            this.telemetryClient.trackException({
                                exception: new Error(ex.response.statusText),
                                properties: errorTelemetryProperty,
                            });
                        }
                        reject({
                            status: ex.response.status,
                            statusText: ex.response.statusText,
                            data: ex.response.data,
                        });
                    }
                    else {
                        this.telemetryClient.trackTrace({
                            message: TelemetryEvents.APIFailedWithoutResponse,
                            properties: requestTelemetryProperties,
                        });
                        const usageEvent = Object.assign(Object.assign({}, event), { type: EventType.System, timeTaken: +new Date() - +startTime, experienceResult: false, 
                            //Do not log the response data.
                            businessTransactionId: JSON.stringify(Object.assign({ status: 'Unknown', correlationId }, requestTelemetryProperties)) });
                        this.telemetryClient.trackEvent(usageEvent);
                        this.telemetryClient.trackException({
                            exception: ex,
                            properties: requestTelemetryProperties,
                        });
                        reject({
                            status: -1,
                            statusText: TelemetryEvents.APIFailedWithoutResponse,
                            data: ex,
                        });
                    }
                }
            }));
        });
    }
    get(url, request, option, event) {
        return this.request(Object.assign({}, request, { url, method: 'get' }), option, event);
    }
    post(url, request, option, event) {
        return this.request(Object.assign({}, request, { url, method: 'post' }), option, event);
    }
    delete(url, request, option, event) {
        return this.request(Object.assign({}, request, { url, method: 'delete' }), option, event);
    }
    put(url, request, option, event) {
        return this.request(Object.assign({}, request, { url, method: 'put' }), option, event);
    }
    patch(url, request, option, event) {
        return this.request(Object.assign({}, request, { url, method: 'patch' }), option, event);
    }
    getPayloadTelemetryProperty(shouldLogPayload = false, payload) {
        return shouldLogPayload ? { payload } : {};
    }
}
//# sourceMappingURL=HttpClient.js.map